import { Controller, FormProvider, useForm } from "react-hook-form";
import { Autocomplete, Checkbox, Grid, TextField } from "@mui/material";
import LocalCafeIcon from "@mui/icons-material/LocalCafe";
import LocalCafeOutlinedIcon from "@mui/icons-material/LocalCafeOutlined";
import { useState } from "react";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { yupResolver } from "@hookform/resolvers/yup";
import { BorderedFormGrid } from "../../../Forms/BorderedFormGrid";
import { WeekDaysTimePicker } from "../../../OpeningTimes/WeekDaysTimePicker";
import OpeningTime, {
  BREAK_START,
  CLOSING_TIME,
  OPENING_TIME,
} from "../../../OpeningTimes/OpeningTime";
import { textFieldDefaultProps } from "../../../Forms/TextFieldDefaultProps";
import { sortedWeekDays } from "../../../OpeningTimes/WeekDays";
import { halfHourIntervalsFromZeroTo24 } from "../../../Utils/timeslots";
import { resetFormStateAndKeepFormValues } from "../../../Forms/resetFormStateAndKeepFormValues";
import { openingTimesSchema } from "../../../OpeningTimes/openingTimesSchema";
import { ActionButtonsEditMode } from "../../../OpeningTimes/ActionButtonsEditMode";
import { ActionButtonsNotInEditMode } from "../../../OpeningTimes/ActionButtonsNotInEditMode";
import { isFormSubmitForbidden } from "../../../Forms/isFormSubmitForbidden";
import { useUpdatePlantOpeningTime } from "../../Hooks/useUpdatePlantOpeningTime";
import { useAddPlantOpeningTime } from "../../Hooks/useAddPlantOpeningTime";
import { useFetchPlantOpeningTimes } from "../../Hooks/useFetchPlantOpeningTimes";
import { useIsUserInRoles } from "../../../Auth/useIsUserInRoles";
import { AuthorizationPolicies } from "../../../Auth/AuthorizationPolicy";

type Props = {
  initialValues: OpeningTime;
  deleteOpeningTimeHandler: (openingTime: OpeningTime) => void;
  removeOpeningTimeFromParentStateAfterSave: () => void;
};

export const PlantSingleOpeningTimeForm = ({
  initialValues,
  deleteOpeningTimeHandler,
  removeOpeningTimeFromParentStateAfterSave,
}: Props) => {
  const isUserInAdminNetworkRole = useIsUserInRoles(AuthorizationPolicies.RequireAdminNetworkRoles);

  const methods = useForm<OpeningTime>({
    defaultValues: initialValues,
    mode: "all",
    resolver: yupResolver(openingTimesSchema),
    disabled: !isUserInAdminNetworkRole,
  });

  const [editMode, setEditMode] = useState<boolean>(false);
  const handleEditModeChange = (inEditMode: boolean) => {
    setEditMode(inEditMode);
  };

  const coffeeBreak = methods.watch("HasBreak");
  const changedWeekDays = methods.watch("WeekDays");
  const weekDaysLabel =
    changedWeekDays.length > 0 ? `(${sortedWeekDays(changedWeekDays).join(", ")})` : "";

  const { mutateAsync: addOpeningTime } = useAddPlantOpeningTime();
  const { mutateAsync: updateOpeningTime } = useUpdatePlantOpeningTime();
  const { refetch: refetchOpeningTimes } = useFetchPlantOpeningTimes(initialValues.ParentId);

  const saveOpeningTime = (openingTime: OpeningTime) => {
    const save = openingTime.IsNew ? addOpeningTime(openingTime) : updateOpeningTime(openingTime);
    save.then(async () => {
      await refetchOpeningTimes();
      resetFormStateAndKeepFormValues(methods);
    });
  };

  const isNewlyAddedOpeningTime = !methods.watch("IsNew");
  const openingTimeValues = methods.getValues();

  const actionButtons = editMode ? (
    <ActionButtonsEditMode
      changeEditMode={handleEditModeChange}
      resetFormDataOnClick={methods.reset}
      removeOpeningTimeFromParentStateAfterSave={removeOpeningTimeFromParentStateAfterSave}
      saveOpeningTime={methods.handleSubmit((values: OpeningTime) => saveOpeningTime(values))}
      disableSaveIconButton={isFormSubmitForbidden(methods.formState)}
    />
  ) : (
    <ActionButtonsNotInEditMode
      openingTimeId={openingTimeValues.Id}
      changeEditMode={() => handleEditModeChange(true)}
      deleteOpeningTime={() => deleteOpeningTimeHandler(openingTimeValues)}
      isNewlyAddedOpeningTime={!isNewlyAddedOpeningTime}
      clearNewlyAddedOpeningTimes={removeOpeningTimeFromParentStateAfterSave}
      isReadOnly={!isUserInAdminNetworkRole}
    />
  );

  return (
    <FormProvider {...methods}>
      <BorderedFormGrid container columns={14} rowSpacing={1} columnSpacing={2}>
        <Grid item xs={3}>
          <WeekDaysTimePicker
            fieldName="StartTime"
            label={`${OPENING_TIME} ${weekDaysLabel}`}
            disabled={!editMode}
          />
        </Grid>
        <Grid item xs={3}>
          <WeekDaysTimePicker
            fieldName="EndTime"
            label={`${CLOSING_TIME} ${weekDaysLabel}`}
            disabled={!editMode}
          />
        </Grid>
        <Grid item xs={1}>
          <Controller
            name="HasBreak"
            control={methods.control}
            render={({ field }) => (
              <Checkbox
                {...field}
                icon={<LocalCafeOutlinedIcon />}
                checkedIcon={<LocalCafeIcon />}
                disabled={!editMode}
                checked={field.value}
                onChange={(e) => {
                  const { checked } = e.target;
                  if (!checked) {
                    methods.resetField("BreakStartTime");
                    methods.resetField("BreakDuration");
                  }
                  field.onChange(checked);
                }}
              />
            )}
          />
        </Grid>
        {coffeeBreak ? (
          <>
            <Grid item xs={3}>
              <WeekDaysTimePicker
                fieldName="BreakStartTime"
                label={`${BREAK_START} ${weekDaysLabel}`}
                disabled={!editMode}
              />
            </Grid>
            <Grid item xs={3}>
              <Controller
                name="BreakDuration"
                control={methods.control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    {...field}
                    onChange={(_, data) => {
                      field.onChange(data);
                    }}
                    options={halfHourIntervalsFromZeroTo24}
                    size="small"
                    disableClearable
                    popupIcon={<AccessTimeIcon />}
                    disabled={!editMode}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        {...textFieldDefaultProps}
                        label="Duration (Hours)"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                )}
              />
            </Grid>
          </>
        ) : (
          <Grid item xs={6} />
        )}
        <Grid item xs={1}>
          <Grid item container justifyContent="flex-end">
            {actionButtons}
          </Grid>
        </Grid>
      </BorderedFormGrid>
    </FormProvider>
  );
};
